import React, {useState, useRef, useEffect, useMemo} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEllipsisH} from "@fortawesome/pro-regular-svg-icons";
import clsx from "clsx";
import {iconMap} from "./Icons";
import {useHover} from "@react-aria/interactions";
import {useButton} from "@react-aria/button";
import {useFocusRing} from "@react-aria/focus";
import {useAuth} from "../security/AuthProvider";

export interface MenuItemType {
    id: string;
    label: string;
    onClick: () => void;
    icon?: string;
    disabled?: boolean;
    requiredPermissions?: string[];
}

interface PopoverMenuProps {
    items: MenuItemType[];
    triggerIcon?: React.ReactNode;
    disabled?: boolean;
    className?: string;
    placement?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
    variant?: 'outlined' | 'contained' | 'embedded';
    color?: 'primary' | 'secondary' | 'attention';
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'auto';
}

// ── MenuItem subcomponent ─────────────────────────────────────────────────────

interface MenuItemProps {
    item: MenuItemType;
    index: number;
    isFocused: boolean;
    itemRef: (el: HTMLButtonElement | null) => void;
    onItemClick: (item: MenuItemType) => void;
    onMouseEnter: (index: number) => void;
}

const MenuItem: React.FC<MenuItemProps> = ({item, index, isFocused, itemRef, onItemClick, onMouseEnter}) => {

    const {hoverProps, isHovered} = useHover({isDisabled: item.disabled});

    return (
        <button
            {...hoverProps}
            ref={itemRef}
            type="button"
            disabled={item.disabled}
            onClick={() => onItemClick(item)}
            onMouseEnter={() => onMouseEnter(index)}
            className={clsx(
                'w-full flex items-center text-sm rounded-md text-left transition-colors duration-150 bg-white',
                {
                    'field-disabled': item.disabled,
                    'hover:bg-gray-100': (isHovered || isFocused) && !item.disabled,
                }
            )}
            style={{
                padding: 'var(--spacing-field-y) var(--spacing-field-x)',
                color: item.disabled ? 'rgba(0, 0, 0, 0.48)' : 'var(--color-field-text)',
            }}
            role="menuitem"
            tabIndex={-1}
        >
            {item.icon && (
                <span className="mr-3 flex-shrink-0 w-4 h-4 flex items-center justify-center">
                    {iconMap[item.icon]}
                </span>
            )}
            <span className="flex-1">{item.label}</span>
        </button>
    );
};

// ── PopoverMenu ───────────────────────────────────────────────────────────────

const PopoverMenu: React.FC<PopoverMenuProps> = ({
                                                     items,
                                                     triggerIcon,
                                                     disabled = false,
                                                     className,
                                                     placement = 'bottom-right',
                                                     variant = 'embedded',
                                                     color = 'primary',
                                                     size = 'md'
                                                 }) => {

    const [isOpen, setIsOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const menuRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const menuId = useRef(`popover-menu-${Math.random().toString(36).slice(2)}`);
    const {hasAnyAuthority} = useAuth();

    // Close this menu when another PopoverMenu opens
    useEffect(() => {
        const handleOtherMenuOpen = (e: CustomEvent<{id: string}>) => {
            if (e.detail.id !== menuId.current) {
                setIsOpen(false);
                setFocusedIndex(-1);
            }
        };
        document.addEventListener('popover-menu-open', handleOtherMenuOpen as EventListener);
        return () => document.removeEventListener('popover-menu-open', handleOtherMenuOpen as EventListener);
    }, []);

    // Filter menu items based on permissions
    const visibleItems = useMemo(() => {
        return items.filter(item => {
            // If item has no permission requirements, always show it
            if (!item.requiredPermissions || item.requiredPermissions.length === 0) {
                return true;
            }
            // Check if user has any of the required permissions
            return hasAnyAuthority(item.requiredPermissions);
        });
    }, [items, hasAnyAuthority]);

    const {buttonProps, isPressed} = useButton({
        onPress: () => {
            const opening = !isOpen;
            if (opening) {
                document.dispatchEvent(new CustomEvent('popover-menu-open', {detail: {id: menuId.current}}));
            }
            setIsOpen(opening);
            setFocusedIndex(-1);
        },
        isDisabled: disabled
    }, triggerRef);
    const {isFocused, isFocusVisible, focusProps} = useFocusRing();
    const {hoverProps, isHovered} = useHover({});

    const variantClass = `btn-${variant}-${color}`;

    // Default trigger icon
    const defaultTriggerIcon = <FontAwesomeIcon icon={faEllipsisH} className="icon-primary"/>;

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                triggerRef.current &&
                !triggerRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
                setFocusedIndex(-1);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!isOpen) return;

            const enabledItems = visibleItems.filter(item => !item.disabled);
            const enabledIndexes = visibleItems
                .map((item, index) => ({item, index}))
                .filter(({item}) => !item.disabled)
                .map(({index}) => index);

            switch (event.key) {
                case 'Escape':
                    event.preventDefault();
                    setIsOpen(false);
                    setFocusedIndex(-1);
                    triggerRef.current?.focus();
                    break;

                case 'ArrowDown':
                    event.preventDefault();
                    if (focusedIndex === -1) {
                        const firstEnabledIndex = enabledIndexes[0];
                        setFocusedIndex(firstEnabledIndex);
                        itemRefs.current[firstEnabledIndex]?.focus();
                    } else {
                        const currentEnabledIndex = enabledIndexes.indexOf(focusedIndex);
                        const nextEnabledIndex = enabledIndexes[(currentEnabledIndex + 1) % enabledIndexes.length];
                        setFocusedIndex(nextEnabledIndex);
                        itemRefs.current[nextEnabledIndex]?.focus();
                    }
                    break;

                case 'ArrowUp':
                    event.preventDefault();
                    if (focusedIndex === -1) {
                        const lastEnabledIndex = enabledIndexes[enabledIndexes.length - 1];
                        setFocusedIndex(lastEnabledIndex);
                        itemRefs.current[lastEnabledIndex]?.focus();
                    } else {
                        const currentEnabledIndex = enabledIndexes.indexOf(focusedIndex);
                        const prevEnabledIndex = enabledIndexes[
                            currentEnabledIndex === 0 ? enabledIndexes.length - 1 : currentEnabledIndex - 1
                            ];
                        setFocusedIndex(prevEnabledIndex);
                        itemRefs.current[prevEnabledIndex]?.focus();
                    }
                    break;

                case 'Enter':
                case ' ':
                    event.preventDefault();
                    if (focusedIndex >= 0 && !visibleItems[focusedIndex].disabled) {
                        handleItemClick(visibleItems[focusedIndex]);
                    }
                    break;
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [isOpen, focusedIndex, visibleItems]);

    const handleItemClick = (item: MenuItemType) => {
        if (!item.disabled) {
            item.onClick();
            setIsOpen(false);
            setFocusedIndex(-1);
        }
    };

    const getPlacementClasses = () => {
        switch (placement) {
            case 'bottom-left':
                return 'top-full left-0 mt-2';
            case 'bottom-right':
                return 'top-full right-0 mt-2';
            case 'top-left':
                return 'bottom-full left-0 mb-2';
            case 'top-right':
                return 'bottom-full right-0 mb-2';
            default:
                return 'top-full right-0 mt-2';
        }
    };

    const getSizeClasses = () => {
        switch (size) {
            case 'sm':
                return 'min-w-40';
            case 'md':
                return 'min-w-48';
            case 'lg':
                return 'min-w-64';
            case 'xl':
                return 'min-w-80';
            case 'auto':
                return 'w-auto whitespace-nowrap';
            default:
                return 'min-w-48';
        }
    };

    // Don't render anything if no items are visible after permission filtering
    if (visibleItems.length === 0) {
        return null;
    }

    return (
        <div className={clsx('relative inline-block', className)}>
            <button
                {...buttonProps}
                {...hoverProps}
                {...focusProps}
                ref={triggerRef}
                className={clsx(
                    'btn-base',
                    variantClass,
                    {
                        'btn-hovered': isHovered,
                        'btn-pressed': isPressed || isOpen,
                        'btn-focused': isFocused,
                        'btn-focus-visible': isFocusVisible
                    }
                )}
                aria-expanded={isOpen}
                aria-haspopup="true"
            >
                {triggerIcon || defaultTriggerIcon}
            </button>

            {/* Menu */}
            {isOpen && (
                <div
                    ref={menuRef}
                    className={clsx(
                        'absolute z-50',
                        getPlacementClasses(),
                        getSizeClasses(),
                        'rounded-md overflow-hidden',
                        'focus:outline-none'
                    )}
                    style={{
                        // backgroundColor: 'var(--color-field-background)',
                        border: '1px solid var(--color-field-border)',
                        boxShadow: 'var(--shadow-field)',
                    }}
                    role="menu"
                    aria-orientation="vertical"
                >
                    {visibleItems.map((item, index) => (
                        <MenuItem
                            key={item.id}
                            item={item}
                            index={index}
                            isFocused={focusedIndex === index}
                            itemRef={el => itemRefs.current[index] = el}
                            onItemClick={handleItemClick}
                            onMouseEnter={setFocusedIndex}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default PopoverMenu;